import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { Checkbox, Select, TextInput } from '@opendate-app/setlist';

import { flagValue } from './customFieldHelpers';

const PLACEHOLDERS = {
  'CustomFields::Phone': '(555) 555-5555',
  'CustomFields::Date': 'MM / DD / YYYY',
  'CustomFields::Email': 'you@example.com',
  'CustomFields::Number': '0'
};

const noop = () => {};

const previewInput = (props) => <TextInput size="sm" readOnly tabIndex={-1} onChange={noop} {...props} />;

const PreviewField = ({ field }) => {
  const common = { label: field.name, required: field.required, hint: field.help_text || undefined };

  switch (field.type) {
    case 'CustomFields::Address':
      return (
        <div className="order-form-preview__stack">
          {previewInput({ ...common, 'aria-label': `${field.name} street`, placeholder: 'Street address' })}
          <div className="order-form-preview__row">
            {previewInput({ 'aria-label': `${field.name} city`, placeholder: 'City' })}
            {previewInput({ 'aria-label': `${field.name} ZIP`, placeholder: 'ZIP' })}
          </div>
        </div>
      );
    case 'CustomFields::Select':
      return (
        <Select
          size="sm"
          {...common}
          placeholder="Select…"
          options={(field.custom_field_options || []).map((o) => ({ value: o.name, label: o.name }))}
          onChange={noop}
        />
      );
    case 'CustomFields::Checkbox':
      return <Checkbox label={field.required ? `${field.name} *` : field.name} checked={false} onChange={noop} tabIndex={-1} />;
    case 'CustomFields::TextArea':
      return previewInput({ ...common, multiline: true, rows: 2 });
    case 'CustomFields::File':
      return previewInput({ ...common, placeholder: 'PDF, JPG or PNG' });
    default:
      return previewInput({ ...common, placeholder: PLACEHOLDERS[field.type] || '' });
  }
};

PreviewField.propTypes = {
  field: PropTypes.object.isRequired
};

const onlyFor = (field, ticketTypes) => {
  if (!field.ticket_type_targeted) return null;
  const ids = (field.ticket_type_ids || []).map(Number);
  const names = ticketTypes.filter((tt) => ids.includes(Number(tt.id))).map((tt) => tt.name);
  return names.length ? `Only for: ${names.join(', ')}` : 'Not asked: no ticket types';
};

const OrderFormPreview = ({ fields, subtitle, ticketTypes }) => {
  const shown = fields.filter((f) => flagValue(f, 'enabled') && flagValue(f, 'fan_visible'));
  const perTicket = shown.filter((f) => f.ask_per === 'ticket');
  const renderField = (field) => {
    const note = onlyFor(field, ticketTypes);
    return note ? (
      <div key={field.id}>
        <PreviewField field={field} />
        <div className="order-form-preview__note">{note}</div>
      </div>
    ) : <PreviewField key={field.id} field={field} />;
  };
  const body = useRef(null);

  useEffect(() => {
    body.current.setAttribute('inert', '');
  }, []);

  return (
    <section className="order-form-preview" aria-label="Checkout preview">
      <div className="order-form-preview__header">
        <div className="order-form-preview__title">Checkout preview</div>
        <div className="order-form-preview__subtitle">{subtitle}</div>
      </div>
      <div className="order-form-preview__body" ref={body}>
        <div className="order-form-preview__card">
          <div className="order-form-preview__card-title">Your info</div>
          <div className="order-form-preview__row">
            {previewInput({ 'aria-label': 'First name', placeholder: 'First name' })}
            {previewInput({ 'aria-label': 'Last name', placeholder: 'Last name' })}
          </div>
          {previewInput({ 'aria-label': 'Email', placeholder: 'Email' })}
          {shown.filter((f) => f.ask_per !== 'ticket').map(renderField)}
        </div>
        {perTicket.length > 0 && (
          <div className="order-form-preview__card" data-testid="order-form-preview-ticket">
            <div className="order-form-preview__card-title">Ticket 1</div>
            {perTicket.map(renderField)}
            <div className="order-form-preview__note">Asked again for every ticket in the order.</div>
          </div>
        )}
      </div>
    </section>
  );
};

OrderFormPreview.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  subtitle: PropTypes.string,
  ticketTypes: PropTypes.arrayOf(PropTypes.object)
};

OrderFormPreview.defaultProps = {
  subtitle: 'Enabled, fan-visible fields',
  ticketTypes: []
};

export default OrderFormPreview;
